import { Banner, Button } from "@omony/ui";
import { Video, Volume2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { getMedia, stopMedia } from "../../shared/media.js";
import { chime } from "../../shared/sound.js";
import { useT } from "./common.js";
import styles from "./flow.module.css";

/** Kamera- und Mikrofontest vor dem Gespräch (A17): Vorschau, Pegel, Testton */
export function DeviceTest() {
  const t = useT();
  const video = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!stream) return;
    if (video.current) video.current.srcObject = stream;
    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    let raf = 0;
    const tick = () => {
      analyser.getByteTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
      setLevel(Math.min(1, peak / 64));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      void ctx.close();
      stopMedia(stream);
    };
  }, [stream]);

  const start = async () => {
    setError(null);
    try {
      setStream(await getMedia({ video: { facingMode: "user" }, audio: true }));
    } catch (e) {
      setError((e as DOMException).name === "NotAllowedError" ? "console.test.denied" : "console.test.error");
    }
  };

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>{t("console.test.title")}</h2>
      <p className={styles.small}>{t("console.test.lead")}</p>
      {stream ? (
        <div className={styles.grid2}>
          <div className={styles.preview}>
            <video ref={video} autoPlay playsInline muted aria-label={t("console.test.preview")} />
          </div>
          <div className={styles.section}>
            <span className={styles.small}>{t("console.test.level")}</span>
            <div
              className={styles.meter}
              role="meter"
              aria-label={t("console.test.level")}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(level * 100)}
            >
              <div className={styles.meterFill} style={{ inlineSize: `${Math.round(level * 100)}%` }} />
            </div>
            <div className={styles.actions}>
              <Button variant="secondary" icon={<Volume2 />} onPress={() => chime("info")}>
                {t("console.test.tone")}
              </Button>
              <Button variant="quiet" onPress={() => setStream(null)}>
                {t("console.test.stop")}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.actions}>
          <Button variant="secondary" icon={<Video />} onPress={() => void start()}>
            {t("console.test.start")}
          </Button>
        </div>
      )}
      {error ? <Banner tone="warning">{t(error)}</Banner> : null}
    </div>
  );
}
